import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { StepVerificationService } from '../../services/step-verification/step-verification.service';
import { DummyStep } from '../../models/steps/dummy-step';
import { map } from 'rxjs/operators';
import { StepImpl } from '../../models/steps/step-impl';
import { ApplicationService } from '../../services/application/application.service';
import { Platform } from '../../services/translations/translations.service';
import { OrderingProcessService } from '../../services/ordering-process/ordering-process.service';
import { SummaryStep } from '../../models/steps/summary-step';

@Injectable({
  providedIn: 'root',
})
export class FormValidationGuard implements CanActivateChild {
  private language = '';

  private currentRoute = '';

  constructor(
    private readonly stepVerificationService: StepVerificationService,
    private readonly orderingProcessService: OrderingProcessService,
    private readonly application: ApplicationService,
    private readonly router: Router
  ) {}

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    if (route.parent) {
      this.language = route.parent.paramMap.get('language') as string;
      return this.application.getPlatform() === Platform.mobile
        ? this.bootApplication().pipe(map(() => true))
        : this.bootDesktop(route);
    }
    return of(false);
  }

  private bootDesktop(route: ActivatedRouteSnapshot): Observable<boolean> {
    this.currentRoute = route.url[0].path;
    const step = this.orderingProcessService.getCurrentStep();
    this.orderingProcessService.setCurrentStep(this.currentRoute);
    if (step instanceof SummaryStep) {
      return of(true);
    }
    const firstInvalidStep = this.stepVerificationService.getFirstInvalidStep();
    return firstInvalidStep instanceof DummyStep
      ? this.waitForStepsReady()
      : of(true);
  }

  private bootApplication(): Observable<void> {
    return this.application.bootWith(this.language);
  }

  private waitForStepsReady(): Observable<boolean> {
    return this.bootApplication().pipe(map(() => this.checkStep()));
  }

  private checkStep(
    firstInvalidStep: StepImpl = this.stepVerificationService.getFirstInvalidStep()
  ): boolean {
    const canActivate = firstInvalidStep.name === this.currentRoute;
    if (!canActivate) {
      this.router.navigate([this.language, firstInvalidStep.name]);
    }
    return canActivate;
  }
}
